// javascript
// Анимация ожидания для pending-индикатора внутри AiChat::Turn.
//
// Поведение:
//   - Таймер MM:SS считается от data-dae20--chat-pending-started-at-value (ms epoch).
//   - Фраза ротируется каждые 3 секунды:
//     * если последнее видимое событие в контейнере событий turn'а — tool_call,
//       показывается его human-readable label (из A4);
//     * иначе — крутится случайная фраза из THINKING_PHRASES (лёгкий юмор).
//
// Архитектура (после Turn refactor):
//   pending-виджет рендерится в _turn.html.erb после events-контейнера.
//   События приходят как append'ы в `ai_chat_turn_<id>_events` через
//   broadcast_append_to. MutationObserver на этом контейнере (а не на собственном
//   поддереве!) — никаких каскадных петель.

import { Controller } from "@hotwired/stimulus"

const THINKING_PHRASES = [
  "Думаю…",
  "Собираюсь с мыслями…",
  "Кручу шестерёнки…",
  "Прикидываю варианты…",
  "Вспоминаю, чему меня учили…",
  "Перебираю кейсы…",
  "Сверяюсь с источниками…",
  "Подбираю формулировку…",
  "Складываю мысли в предложения…",
  "Ещё чуть-чуть…",
]

export default class extends Controller {
  static targets = ["timer", "phrase"]
  static values = {
    startedAt: Number, // ms epoch; defaults to Date.now() at connect
    tickInterval: { type: Number, default: 1000 },
    phraseInterval: { type: Number, default: 9000 },
  }

  connect() {
    if (!this.hasStartedAtValue || this.startedAtValue <= 0) {
      this.startedAtValue = Date.now()
    }
    this.thinkingIndex = Math.floor(Math.random() * THINKING_PHRASES.length)

    // Find the events container — sibling within the message-root.
    // (Pending widget renders AFTER events container in the turn DOM.)
    const root = this.element.closest("[data-message-root]")
    this.eventsContainer = root && root.querySelector("[id^='ai_chat_turn_'][id$='_events']")

    this._tick()
    this._rotatePhrase()

    this.tickTimer = setInterval(() => this._tick(), this.tickIntervalValue)
    this.phraseTimer = setInterval(() => this._rotatePhrase(), this.phraseIntervalValue)

    // React immediately when a new event appears (e.g. tool_call broadcast).
    if (this.eventsContainer) {
      this.observer = new MutationObserver(() => this._rotatePhrase())
      this.observer.observe(this.eventsContainer, { childList: true })
    }
  }

  disconnect() {
    if (this.tickTimer) clearInterval(this.tickTimer)
    if (this.phraseTimer) clearInterval(this.phraseTimer)
    if (this.observer) this.observer.disconnect()
  }

  _tick() {
    if (!this.hasTimerTarget) return
    const elapsedMs = Math.max(0, Date.now() - this.startedAtValue)
    const total = Math.floor(elapsedMs / 1000)
    const mm = String(Math.floor(total / 60)).padStart(2, "0")
    const ss = String(total % 60).padStart(2, "0")
    this.timerTarget.textContent = `${mm}:${ss}`
  }

  _rotatePhrase() {
    if (!this.hasPhraseTarget) return

    const toolLabel = this._activeToolLabel()
    if (toolLabel) {
      this.phraseTarget.textContent = toolLabel
      return
    }

    this.thinkingIndex = (this.thinkingIndex + 1) % THINKING_PHRASES.length
    this.phraseTarget.textContent = THINKING_PHRASES[this.thinkingIndex]
  }

  // Returns label of the most recent tool_call event if it's the LAST visible
  // event in the events container (= tool is currently running).
  // If the last event is assistant_text, the model is "thinking" between
  // tools and we fall back to THINKING_PHRASES.
  _activeToolLabel() {
    if (!this.eventsContainer) return null

    const events = this.eventsContainer.querySelectorAll("[data-event-id]")
    if (!events.length) return null

    const last = events[events.length - 1]
    if (last.dataset.eventKind !== "tool_call") return null

    const labelEl = last.querySelector("[data-tool-label]")
    return labelEl ? labelEl.textContent.trim() : null
  }
}
